/**
 * Чистые функции действий над аккаунтом в таблице (KAN-609).
 * Без React и I/O: доступность действий, тексты подтверждений и ошибок.
 */
import {
  CHECK_FALLBACK_MESSAGE,
  CHECK_TIMEOUT_MESSAGE,
  CHECK_UNAVAILABLE_MESSAGE,
} from '@/lib/telegramAccountFormat';
import { describeApiError } from '@/lib/telegramConnect';
import type { TelegramAccount } from '@/types/telegramAccount';

// KAN-871: признак личного аккаунта менеджера (общий для экрана аккаунтов).
export { isPersonalTelegramAccount } from '@/lib/telegramPersonalAccount';

/** KAN-671: выключение не рвёт соединение, оно блокирует сообщения в обе стороны. */
export const DISABLE_CONFIRM = {
  title: 'Выключить аккаунт?',
  description:
    'Сообщения через этот аккаунт не будут отправляться и приниматься. Соединение с Telegram остаётся.',
  confirm: 'Выключить',
};

export const FORCE_DISABLE_CONFIRM = {
  title: 'Выключить всё равно?',
  description:
    'Аккаунт недавно подключён. Если выключить его сейчас, свежая сессия может погибнуть, и аккаунт придётся подключать заново.',
  confirm: 'Выключить всё равно',
};

export const DECOMMISSION_CONFIRM = {
  title: 'Списать аккаунт?',
  description:
    'Соединение аккаунта будет закрыто, аккаунт будет отключён и исключён из рассылки. Позже его можно вернуть кнопкой "Вернуть": после возврата он останется выключенным.',
  confirm: 'Списать',
};

const LIVE_STATUSES = ['connected', 'connecting', 'paused'];

/** Действия доступны только аккаунтам нового держателя, кроме основного. */
export function hasAccountActions(
  account: Pick<TelegramAccount, 'holder' | 'account_id'>
): boolean {
  return account.holder === 'gateway' && account.account_id !== 'primary';
}

/** Выключение требует подтверждения, если соединение живое или статус неизвестен. */
export function needsDisableConfirm(
  account: Pick<TelegramAccount, 'status'>
): boolean {
  const { status } = account;
  if (!status) return true;
  return LIVE_STATUSES.includes(status);
}

/** Кнопка переключения: включить или выключить. */
export function toggleActionView(enabled: boolean): {
  label: string;
  next: boolean;
} {
  return enabled
    ? { label: 'Выключить', next: false }
    : { label: 'Включить', next: true };
}

interface ErrLike {
  response?: { status?: number; data?: unknown };
}

/** 409 refused_fresh: живой аккаунт моложе 24 ч, выключение без force отклонено. */
export function isFreshRefusal(err: unknown): boolean {
  const res = (err as ErrLike | null)?.response;
  if (res?.status !== 409) return false;
  const data = res.data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (d.code === 'refused_fresh' || d.error === 'refused_fresh') return true;
  }
  return /свеж|моложе|недавно/i.test(describeApiError(err, ''));
}

export function toggleErrorMessage(err: unknown): string {
  return describeApiError(err, 'Не удалось изменить состояние аккаунта');
}

export function outreachErrorMessage(err: unknown): string {
  return describeApiError(err, 'Не удалось изменить режим рассылки');
}

export interface DecommissionNotice {
  kind: 'success' | 'warning';
  text: string;
}

/**
 * Уведомление после списания или возврата. holder_synced === false при списании:
 * флаг сохранён, но держатель не закрыл соединение (предупреждение вместо успеха).
 */
export function decommissionNotice(
  decommissioned: boolean,
  result: { holder_synced?: boolean } | null | undefined
): DecommissionNotice {
  if (!decommissioned) return { kind: 'success', text: 'Аккаунт возвращён' };
  if (result?.holder_synced === false) {
    return {
      kind: 'warning',
      text: 'Аккаунт списан, но соединение закрыть не удалось',
    };
  }
  return { kind: 'success', text: 'Аккаунт списан' };
}

export function decommissionErrorMessage(err: unknown): string {
  return describeApiError(err, 'Не удалось изменить статус списания');
}

/** Ошибка проверки здоровья: 503, 504 и 409 (текст сервера) отдельными текстами. */
export function checkErrorMessage(err: unknown): string {
  const status = (err as ErrLike | null)?.response?.status;
  if (status === 503) return CHECK_UNAVAILABLE_MESSAGE;
  if (status === 504) return CHECK_TIMEOUT_MESSAGE;
  return describeApiError(err, CHECK_FALLBACK_MESSAGE);
}
